// import { Node } from 'unist';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkStringify from 'remark-stringify';
import remarkHtml from 'remark-html';
// 使用 mdast 中的 Root 类型
import type { Root, Node, Paragraph, Heading, Text, Emphasis, Strong, InlineCode, Code, List, ListItem, Link, Image, Blockquote } from 'mdast';

// 用于处理 Markdown 转换和 AST 生成的工具类
export class ChangeCollector {
  private parser;
  private stringifier;
  private htmlProcessor;
  private lastContent: string = '';
  private lastAst: Root | null = null;

  constructor() {
    // 初始化 remark 解析器和序列化器
    this.parser = unified().use(remarkParse);
    
    // 使用配置构建 remarkStringify 处理器
    this.stringifier = unified().use(remarkStringify, {
      bullet: '-',       // 使用 - 作为无序列表标记
      emphasis: '*',     // 使用 * 作为斜体标记
      strong: '*',       // 使用 * 作为粗体标记 (不能用 '**')
      fence: '`',        // 代码块标记 (不能用 '```')
      fences: true,      // 使用围栏式代码块
      listItemIndent: 'one', // 列表项缩进风格
      tightDefinitions: true,
    });

    // 初始化 HTML 处理器
    this.htmlProcessor = unified().use(remarkParse).use(remarkHtml, {
      sanitize: false, // 不进行净化，保留原始HTML
    });
  }

  /**
   * 将 Markdown 内容处理并生成 AST
   * @param content 要处理的 Markdown 内容
   * @returns 包含处理后的 Markdown 内容和 AST 的对象
   */
  public processContent(content: string): { markdown: string, ast: Root | null } {
    // 如果内容没有变化，直接返回缓存的结果
    if (content === this.lastContent && this.lastAst) {
      return {
        markdown: content,
        ast: this.lastAst
      };
    }

    try {
      // 使用 remark 解析 Markdown 生成 AST
      const ast = this.parser.parse(content) as Root;
      
      // 缓存结果
      this.lastContent = content;
      this.lastAst = ast;
      
      return {
        markdown: content,
        ast
      };
    } catch (error) {
      console.error('解析 Markdown 内容时出错:', error);
      return {
        markdown: content,
        ast: null
      };
    }
  }

  /**
   * 从 AST 生成 Markdown
   * @param ast 要序列化的 AST
   * @returns Markdown 文本
   */
  public generateMarkdown(ast: Root): string {
    try {
      // 使用 remark 从 AST 生成 Markdown
      const markdown = this.stringifier.stringify(ast);
      return markdown;
    } catch (error) {
      console.error('从 AST 生成 Markdown 时出错:', error);
      return '';
    }
  }

  /**
   * 检查两个 AST 的区别，判断是否需要更新
   * @param oldAst 原有的 AST
   * @param newAst 新的 AST
   * @returns 是否有实质性变化
   */
  public hasSignificantChanges(oldAst: Root | null, newAst: Root | null): boolean {
    if (!oldAst || !newAst) return true;
    
    // 实现更高效的AST比较逻辑
    return this.compareNodes(oldAst, newAst);
  }

  /**
   * 比较两个AST节点的差异
   * @param node1 第一个节点
   * @param node2 第二个节点
   * @returns 是否有差异
   */
  private compareNodes(node1: Node, node2: Node): boolean {
    // 类型不同，直接返回true
    if (node1.type !== node2.type) return true;
    
    // 根据节点类型比较特定属性
    switch (node1.type) {
      case 'text': {
        return (node1 as Text).value !== (node2 as Text).value;
      }
        
      case 'heading': {
        const h1 = node1 as Heading;
        const h2 = node2 as Heading;
        if (h1.depth !== h2.depth) return true;
        return this.compareChildren(h1.children, h2.children);
      }
        
      case 'paragraph':
      case 'emphasis':
      case 'strong':
      case 'blockquote':
      case 'list':
      case 'listItem': {
        return this.compareChildren(
          (node1 as Paragraph | Emphasis | Strong | Blockquote | List | ListItem).children,
          (node2 as Paragraph | Emphasis | Strong | Blockquote | List | ListItem).children
        );
      }
        
      case 'link': {
        const l1 = node1 as Link;
        const l2 = node2 as Link;
        if (l1.url !== l2.url || l1.title !== l2.title) return true;
        return this.compareChildren(l1.children, l2.children);
      }
        
      case 'image': {
        const img1 = node1 as Image;
        const img2 = node2 as Image;
        return img1.url !== img2.url || img1.alt !== img2.alt || img1.title !== img2.title;
      }
        
      case 'inlineCode':
      case 'code': {
        const c1 = (node1 as InlineCode | Code).value;
        const c2 = (node2 as InlineCode | Code).value;
        return c1 !== c2;
      }
        
      case 'thematicBreak': {
        return false; // 横线节点没有内容可比较
      }
        
      case 'root': {
        return this.compareChildren(
          (node1 as Root).children,
          (node2 as Root).children
        );
      }
        
      default: {
        // 未知节点类型，保守返回true
        return true;
      }
    }
  }
  
  /**
   * 比较两个子节点数组
   * @param children1 第一个子节点数组
   * @param children2 第二个子节点数组
   * @returns 是否有差异
   */
  private compareChildren(children1: Node[], children2: Node[]): boolean {
    if (children1.length !== children2.length) return true;
    
    for (let i = 0; i < children1.length; i++) {
      if (this.compareNodes(children1[i], children2[i])) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * 清除缓存
   */
  public clearCache(): void {
    this.lastContent = '';
    this.lastAst = null;
  }

  /**
   * 将Markdown内容转换为HTML
   * @param markdown 要转换的Markdown内容
   * @returns HTML格式的内容
   */
  public markdownToHtml(markdown: string): string {
    try {
      // 使用remark-html处理器将Markdown转换为HTML
      const result = this.htmlProcessor.processSync(markdown);
      return String(result.value);
    } catch (error) {
      console.error('将Markdown转换为HTML时出错:', error);
      return markdown; // 出错时返回原始内容
    }
  }
} 